import * as THREE from "three";
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry.js';
import { Font } from 'three/examples/jsm/loaders/FontLoader.js';
import ICanvasInputListener from "../logic/ICanvasInputListener";
import Command from "./Command";
import IMouseInputListener from "../inputListeners/MouseInputListener";
import CommandIdEnum from "../enums/CommandIdEnums";
import { useModelPropertiesStore } from "../store/modelPropertiesStore/modelPropertiesStore";
import { SceneManager } from "../model/SceneManager";
import { sceneUnitsFromMm } from "../utils/scaler";
import { formatCm } from "../utils/dragMeasurement";
import RaycastIntersection from "../logic/RaycastIntersection";
import RefAxisHelper from "./commandHelpers/RefAxisHelper";
import PlaneGridHelper from "./commandHelpers/PlaneGridHelper";
import CanvasInputListenerModeEnum from "../enums/CanvasInputListenerModeEnum";
import ISceneObject from "../model/sceneObject/ISceneObject";
import { getSceneObjectStyle, SceneObjectStyle, SceneObjectStyleEnum } from '../model/sceneObject/SceneObjectStyle';
import CommandStack from "../logic/CommandStack";
import { useCurrentCommandState } from "../store/currentCommandStateStore";
import { useViewerStateStore } from "../store/viewerStateStore";
import { StageTypeEnum } from "../store/viewerState/ViewerMode/ViewerMode";

export default class InputCommand extends Command implements IMouseInputListener
{
    canvasInputListener: ICanvasInputListener;

    startMeasurePoint: THREE.Vector3 | null = null;
    raycastRecoil: number = sceneUnitsFromMm(10.0);
    refAxisHelper: RefAxisHelper;
    planeGridHelper: PlaneGridHelper;
    modelRotationOffset: number;

    drawables: ISceneObject[] = [];
    #drawableStyle: SceneObjectStyle = getSceneObjectStyle(SceneObjectStyleEnum.Drawable);

    constructor(    id: CommandIdEnum,
                    commandStack: CommandStack,
                    canvasInputListener: ICanvasInputListener) {
        super(id, commandStack);
        this.canvasInputListener = canvasInputListener;

        const rotationOffset = useModelPropertiesStore.getState().rotationOffset;

        // Define reference axes
        const vZ = new THREE.Vector3(0,0,1);
        const vX = new THREE.Vector3(1,0,0);
        vX.applyAxisAngle(vZ, rotationOffset);
        const vY = vX.clone().applyAxisAngle(vZ, Math.PI / 2);
        this.refAxisHelper = new RefAxisHelper(new THREE.Vector3(0,0,0), [vX, vY, vZ])
        this.refAxisHelper.addToScene(SceneManager.scene);

        this.planeGridHelper = new PlaneGridHelper();
        this.planeGridHelper.addToScene(SceneManager.scene);

        // Rotate preferred annotation up to match with the model's rotation
        this.modelRotationOffset = rotationOffset;
    }

    // The running-command bar describes the command the user launched - the one on the stack. A
    // sub-command (the move that positions a fresh clone) runs under that command's name, so its
    // own per-state prompts must not overwrite the wording the owner published when it was pushed.
    showInfoMessage(resourceKey: string, params?: Record<string, unknown>) {
        if (!this.isStacked()) return;

        useCurrentCommandState.getState().setCurrentCommandPrompt(resourceKey, params);
    }

    hideInfoMessage() {
        if (!this.isStacked()) return;

        useCurrentCommandState.getState().clearCurrentCommandPrompt();
    }

    // Bounce back the point towards the camera
    // TODO: Move recoil logic to CanvasInputListener. Preferably, recoiled point should be part of the RaycastIntersection
    addRecoil(point: THREE.Vector3, recoilDistance: number = this.raycastRecoil) {

        if (recoilDistance <= 0)
            return; // No recoil if distance is zero or negative
        // In TargetObjects -mode add recoil in the direction of the camera
        else if (this.canvasInputListener.raycastMode === CanvasInputListenerModeEnum.RAYCAST_TARGET_OBJECTS) {
            const cameraDirection = this.canvasInputListener.getCameraWorldDirection();
            const recoil = cameraDirection.multiplyScalar(recoilDistance);
            recoil.negate();
            point.add(recoil);
        }
        // In planar mode, add recoil in the direction of the plane normal
        else if (this.canvasInputListener.raycastMode === CanvasInputListenerModeEnum.RAYCAST_PLANE) {
            const planeNormal = this.canvasInputListener.getTargetPlane().normal.clone();
            const recoil = planeNormal.multiplyScalar(recoilDistance);
            point.add(recoil);
        } else {
            console.error("Failed to add recoil to point");
        }
    }

    getCursorName(): string {
        return 'crosshair';
    }

    setCursor(cursor: string) {
        useCurrentCommandState.getState().setCanvasCursor(cursor);
    }

    connectMouse() {
        this.canvasInputListener.enable();
        this.canvasInputListener.addMouseListener(this);
        this.setCursor(this.getCursorName());
    }

    disconnectMouse() {
        this.canvasInputListener.disable();
        this.canvasInputListener.removeMouseListener(this);
        useCurrentCommandState.getState().clearCanvasCursor();
    }

    enableReferenceLine() {
        this.refAxisHelper.enable();
    }

    disableReferenceLine() {
        this.refAxisHelper.disable();
    }

    startMeasure(point: THREE.Vector3) {
        this.startMeasurePoint = point.clone();
        useCurrentCommandState.getState().setMeasureText("");
    }

    stopMeasure() {
        this.startMeasurePoint = null;
        useCurrentCommandState.getState().clearMeasureText();
    }

    setReferencePoint(point: THREE.Vector3) {
        this.refAxisHelper.enable();
        this.refAxisHelper.setReferencePoint(point);
    }

    static readonly REF_LINE_SNAP_PIXEL_RADIUS: number = 5; // Desired on-screen snap radius for reference lines, in pixels

    getRefLineAlignedPoint(point: THREE.Vector3): THREE.Vector3 {
        const worldUnitsPerPixel = this.canvasInputListener.getWorldUnitsPerPixel();
        const distanceTolerance = worldUnitsPerPixel
            ? worldUnitsPerPixel * InputCommand.REF_LINE_SNAP_PIXEL_RADIUS
            : sceneUnitsFromMm(50); // Fallback if camera distance is not yet available
        const refAlignedPt = this.refAxisHelper.getRefLineAlignedPoint(point, distanceTolerance) ?? point;
        this.refAxisHelper.toggleAxesVisibility(refAlignedPt, distanceTolerance);
        return refAlignedPt;
    };

    showPlaneGrid(point: THREE.Vector3, normal: THREE.Vector3 | null) {
        this.planeGridHelper.update(point, normal);
        this.planeGridHelper.show();
    }

    hidePlaneGrid() {
        this.planeGridHelper.hide();
    }

    rightClick(event: MouseEvent, raycaster: THREE.Raycaster, targets: THREE.Object3D[]) : void {
        this.onSwitchPlane();
    }

    cancel(): void {
        // No-op by default; commands that support mid-gesture cancellation should override.
    }

    /** Override in subclasses to also update the target plane after cycling. */
    onSwitchPlane(): void {
        if (useViewerStateStore.getState().viewerMode?.stageType === StageTypeEnum.Stage2d) return;
        this.planeGridHelper.cycleRotationOffset();
    }

    showSwitchPlaneButton(): void {
        if (useViewerStateStore.getState().viewerMode?.stageType === StageTypeEnum.Stage2d) return;
        useCurrentCommandState.getState().showSwitchPlaneButton(() => this.onSwitchPlane());
    }

    hideSwitchPlaneButton(): void {
        useCurrentCommandState.getState().hideSwitchPlaneButton();
    }

    pointerMove(raycastResult: RaycastIntersection | undefined): boolean {

        if (!this.startMeasurePoint || !raycastResult) return false;

        const measureText = formatCm(this.startMeasurePoint.distanceTo(raycastResult.point));
        useCurrentCommandState.getState().setMeasureText(measureText);

        return false;
    }

    static getTextGeometry(text: string, font: Font): TextGeometry {

        return new TextGeometry(text, {
            font: font,
            size: 0.07,
            depth: 0.01,
            curveSegments: 6,
            bevelEnabled: false,
        });
    }

    updateRefLine(point: THREE.Vector3 | null) {
    }

    //handleLeftClick(event: MouseEvent, raycaster: Raycaster) {}

    point(raycastResult: RaycastIntersection | undefined): boolean {

        return false;
    }

    dispose()
    {
        this.refAxisHelper.dispose();
        this.planeGridHelper.dispose();
        this.disconnectMouse();
    }

    end()
    {
        this.hideInfoMessage();
        this.stopMeasure();
        this.disableReferenceLine();
        this.hidePlaneGrid();
        this.hideSwitchPlaneButton();
        this.planeGridHelper.resetRotationOffset();
        this.removeAllDrawables();
        this.dispose();
        super.end();
    }

    setDrawableStyle(style: SceneObjectStyle) {
        this.#drawableStyle = style;
    }

    addToDrawables(sceneObject: ISceneObject) {
        this.drawables.push(sceneObject);
        sceneObject.geometry.addToScene();
        sceneObject.geometry.setStyle(this.#drawableStyle);
        sceneObject.onAnnotationDirectionChange(this.modelRotationOffset);
    }

    removeFromDrawables(sceneObject: ISceneObject) {
        const index = this.drawables.indexOf(sceneObject);
        if (index > -1) {
            this.drawables.splice(index, 1);
            sceneObject.geometry.removeFromScene();
        }
    };

    removeAllDrawables() {
        this.drawables.forEach((drawable) => {
            drawable.geometry.removeFromScene();
            drawable.dispose();
        });
        this.drawables = [];
    }

}
